// Hands-free in the chat (Features 4, David, 26 Oct): each chat's own switch, in its model menu and kept with the chat,
// and the default for new ones from Settings, Agents. A message the user sends in a chat whose switch is on goes to the
// computer marked hands-free; the board passes it on only for the user's own words (worker/workspace.ts), and the
// computer still asks before anything that sends, deletes or buys. Words the board sends by itself (a plan's "carry on")
// never go hands-free: the user did not send them.

import { HANDS_FREE_RUNNER, runnerHoldsHandsFreeJobs, runnerKnowsHandsFree } from "../../shared/withoutAsking";
import type { RunnerState } from "../../shared/types";
import { api } from "../lib/api";
import { get, set } from "../state/store";
import { chat, convo, saveConversation, type ConvoRef } from "./store";

export { HANDS_FREE_RUNNER };

/** The default for new chats, Buttons and jobs, as Settings, Agents has it: off until the owner turns it on. */
export const handsFreeDefault = (runner: RunnerState | null = get().runner): boolean => runner?.handsFree === true;

/** Whether a conversation runs hands-free: its own switch, or the default while nobody has touched it. */
export function chatHandsFree(c: { handsFree?: boolean | null } | null | undefined, runner: RunnerState | null = get().runner): boolean {
  if (!c) return false;
  return c.handsFree ?? handsFreeDefault(runner);
}

/** The chat on screen's switch, turned by the user in its model menu: kept with the chat from now on. */
export function setChatHandsFree(on: boolean) {
  chat.set({ handsFree: on });
  saveConversation();
}

/**
 * Whether a chat's message goes hands-free: its own switch, or the default in Settings while nobody touched it, which
 * the chat keeps from its first message on, so a later change of the default never turns an old chat on or off.
 */
export function keepHandsFree(c: ConvoRef = convo(chat.get().chatId)): boolean {
  const now = c.get();
  const on = chatHandsFree(now);
  if (now && (now.handsFree ?? null) === null) c.set({ handsFree: on });
  return on;
}

/** A Button's chat wears the Button's Hands-free, so what the user writes in it next goes as the run did. */
export function awayHandsFree(id: string, on: boolean) {
  convo(id).set({ handsFree: on });
}

/** Whether the linked computer's runner knows Hands-free; an older one runs every chat and job as it did before. */
export const runnerHandsFree = (runner: RunnerState | null = get().runner): boolean => runnerKnowsHandsFree(runner?.version ?? null);

/** Whether it runs a job hands-free, held to its rule (Fixes 8); an older one runs a job with the board's own tools alone. */
export const runnerHandsFreeJobs = (runner: RunnerState | null = get().runner): boolean => runnerHoldsHandsFreeJobs(runner?.version ?? null);

/** Turns the default for new chats, Buttons and jobs on or off (Settings, Agents), for every device. */
export async function setHandsFreeDefault(on: boolean) {
  const before = get().runner;
  // shown at once; put back if the board did not take it
  if (before) set({ runner: { ...before, handsFree: on } });
  try {
    await api("/api/chat/hands-free", { method: "PUT", body: { on } });
  } catch (e) {
    const now = get().runner;
    if (now) set({ runner: { ...now, handsFree: before?.handsFree } });
    throw e;
  }
}

/**
 * Words the board itself queued for the chat (a plan's "carry on" once its questions are answered), which go the way
 * the user's do but never hands-free. Taken once, when they are sent.
 */
let boardWords: string | null = null;
export function queuedByBoard(words: string) {
  boardWords = words.trim();
}
export function takeBoardWords(sent: string): boolean {
  const was = boardWords !== null && boardWords === sent.trim();
  if (was) boardWords = null;
  return was;
}
